import React from "react";
import { Box, Typography, Paper, Stack, Chip, Fade } from "@mui/material";
import CircularProgress, {
  CircularProgressProps,
} from "@mui/material/CircularProgress";
import { FileWithPreview } from "../types";
import { ChunkedFileWithPreview } from "../types/chunkedUploadTypes";
import { useFileStatusStore } from "../contexts/FileStatusContext";
import { getFileIconAndColor } from "src/reusable/Functions";

function CircularProgressWithLabel(
  props: CircularProgressProps & { value: number },
) {
  const { sx, ...otherProps } = props;
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        variant="determinate"
        {...otherProps}
        size={32}
        thickness={4.5}
        sx={{
          color: props.value === 100 ? "#4caf50" : "#1976d2",
          transition: "color 0.3s ease",
          ...sx,
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          component="div"
          sx={{
            color: props.value === 100 ? "#4caf50" : "text.primary",
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

type ChatAttachmentItemProps = {
  file: FileWithPreview | ChunkedFileWithPreview;
  index: number;
  onRemove?: (index: number) => void;
  isEditable?: boolean;
};

const ChatAttachmentItem: React.FC<ChatAttachmentItemProps> = React.memo(
  ({ file, index, onRemove, isEditable = false }) => {
    const getFileStatus = useFileStatusStore((store) => store.getFileStatus);
    const getFileStatusByName = useFileStatusStore(
      (store) => store.getFileStatusByName,
    );

    const fileStatus = file.id
      ? getFileStatus(file.id)
      : getFileStatusByName(file.name);

    const isUploading = fileStatus?.isUploading ?? file.isUploading ?? false;
    const embeddingProgress =
      fileStatus?.embeddingProgress ?? file.embeddingProgress ?? 0;

    // Check if this is a chunked file with upload progress
    const chunkedFile = file as ChunkedFileWithPreview;

    // Prioritize FileStatusStore for progress tracking to avoid race conditions
    const storeUploadProgress = fileStatus?.uploadProgress;
    const fileUploadProgress = chunkedFile.uploadProgress;

    // Use store progress if available, otherwise fall back to file progress
    const uploadProgress =
      storeUploadProgress !== undefined
        ? storeUploadProgress
        : (fileUploadProgress ?? 100);

    const hasUploadProgress =
      uploadProgress !== undefined &&
      uploadProgress !== null &&
      uploadProgress < 100;

    // Check if this file has been uploaded (uploadProgress >= 100 or no progress tracking)
    const isUploadComplete = uploadProgress >= 100 || !hasUploadProgress;

    // Check if file is queued (pending upload)
    // Check both the file object and the chunkedFile for uploadStatus
    const fileUploadStatus =
      (file as any).uploadStatus || chunkedFile.uploadStatus;
    const isQueued = fileUploadStatus === "pending" && uploadProgress === 0;

    // Determine the current stage based on file status
    const isChunkedUploading =
      hasUploadProgress && uploadProgress < 100 && isUploading;
    const isRegularUploading =
      !hasUploadProgress && isUploading && embeddingProgress === 0;
    // Show processing when upload is complete and still uploading, or when we have embedding progress (but not 100%)
    const isProcessing =
      (isUploadComplete &&
        isUploading &&
        !isRegularUploading &&
        embeddingProgress < 100) ||
      (isUploadComplete && embeddingProgress > 0 && embeddingProgress < 100);

    const { icon } = getFileIconAndColor({ fileName: file.name, fontSize: 28 });
    const extension = file.name?.split(".").pop()?.toUpperCase() || "";

    // Generate a stable identifier for test IDs
    const fileIdentifier = file.id || `idx-${index}`;

    return (
      <Paper
        id={`atom-assist-attachment-${index}`}
        data-testid={`aa-file-item-${fileIdentifier}`}
        variant="outlined"
        sx={{
          p: 1,
          minWidth: 40,
          width: 195,
          position: "relative",
          "&:hover": {
            border: "1px solid #108BDB",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          },
          "&:hover .remove-button": {
            opacity: 1,
          },
        }}
      >
        <Fade
          in={
            isQueued ||
            isUploading ||
            isChunkedUploading ||
            isProcessing ||
            isRegularUploading
          }
        >
          <Box
            data-testid={`aa-file-status-${fileIdentifier}`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.97)",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              borderRadius: 1,
              px: 2,
              py: 1,
              gap: 2,
            }}
          >
            {isChunkedUploading ? (
              <>
                <CircularProgressWithLabel
                  value={uploadProgress}
                  sx={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Uploading
                </Typography>
              </>
            ) : isRegularUploading ? (
              <>
                <CircularProgress
                  size={32}
                  thickness={4}
                  sx={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Uploading file
                </Typography>
              </>
            ) : isProcessing ? (
              <>
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <CircularProgress
                    variant="determinate"
                    value={embeddingProgress}
                    size={32}
                    thickness={4.5}
                    sx={{ color: "#9c27b0" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        color:
                          embeddingProgress === 100
                            ? "#4cae51ff"
                            : "text.primary",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {`${Math.round(embeddingProgress)}%`}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Processing
                </Typography>
              </>
            ) : isQueued ? (
              <>
                <CircularProgress
                  size={32}
                  thickness={4}
                  variant="indeterminate"
                  sx={{ flexShrink: 0, color: "#757575" }}
                />
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  Queued
                </Typography>
              </>
            ) : isUploading ? (
              <>
                <CircularProgress
                  size={32}
                  thickness={4}
                  variant="indeterminate"
                  sx={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Preparing
                </Typography>
              </>
            ) : null}
          </Box>
        </Fade>

        {isEditable && onRemove && (
          <Chip
            className="remove-button"
            data-testid={`aa-file-remove-${fileIdentifier}`}
            data-attr="assistant:chip:remove-attachment"
            label="✕"
            color="error"
            size="small"
            onClick={() => onRemove(index)}
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              height: 20,
              width: 20,
              minWidth: 20,
              fontSize: "0.7rem",
              opacity:
                isUploading ||
                isChunkedUploading ||
                isProcessing ||
                isRegularUploading
                  ? 1
                  : 0,
              transition: "opacity 0.2s",
              zIndex: 3,
              "& .MuiChip-label": {
                padding: 0,
              },
            }}
          />
        )}

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box>{icon}</Box>

          <Stack
            spacing={0.25}
            sx={{ flexGrow: 1, minWidth: 0 }}
            alignItems="flex-start"
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#1F1F1F",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "0.815rem",
                width: "100%",
                textAlign: "left",
              }}
            >
              {file.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#666666",
                fontSize: "0.75rem",
              }}
            >
              {extension}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  },
);

export default ChatAttachmentItem;
